import React, { useMemo, useState } from "react";
import { submitContactForm } from "../services/contactService";
import { useApp } from "../context/AppContext";

export default function Contact() {
  const { siteSettings } = useApp();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  const siteName =
    siteSettings?.site_name ||
    siteSettings?.siteName ||
    "kami";

  const contactEmail =
    siteSettings?.contact_email ||
    siteSettings?.contactEmail ||
    siteSettings?.email ||
    "";

  const phoneNumber =
    siteSettings?.phone_number ||
    siteSettings?.phoneNumber ||
    siteSettings?.phone ||
    "";

  const whatsappNumber =
    siteSettings?.whatsapp_number ||
    siteSettings?.whatsappNumber ||
    "";

  const address =
    siteSettings?.address ||
    siteSettings?.full_address ||
    "";

  const siteDescription =
    siteSettings?.site_description ||
    siteSettings?.siteDescription ||
    siteSettings?.site_tagline ||
    siteSettings?.siteTagline ||
    "";

  const mapsEmbedUrl =
    siteSettings?.google_maps_embed_url ||
    siteSettings?.googleMapsEmbedUrl ||
    siteSettings?.maps_embed_url ||
    siteSettings?.mapsEmbedUrl ||
    "";

  const mapsUrlFromSettings =
    siteSettings?.google_maps_url ||
    siteSettings?.googleMapsUrl ||
    siteSettings?.maps_url ||
    siteSettings?.mapsUrl ||
    "";

  const latitude =
    siteSettings?.latitude ||
    siteSettings?.lat ||
    "";

  const longitude =
    siteSettings?.longitude ||
    siteSettings?.lng ||
    siteSettings?.lon ||
    "";

  const mapsUrl = useMemo(() => {
    if (mapsUrlFromSettings) return mapsUrlFromSettings;

    if (latitude && longitude) {
      return `https://www.google.com/maps?q=${latitude},${longitude}`;
    }

    if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }

    return "";
  }, [mapsUrlFromSettings, latitude, longitude, address]);

  const displayPhone = whatsappNumber || phoneNumber || "-";

  const normalizedWhatsappLink = useMemo(() => {
    const rawNumber = whatsappNumber || phoneNumber || "";
    if (!rawNumber) return "";

    const cleaned = rawNumber.replace(/[^\d]/g, "");
    if (!cleaned) return "";

    return `https://wa.me/${cleaned}`;
  }, [whatsappNumber, phoneNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Nama wajib diisi.";
    if (!form.email.trim()) return "Email wajib diisi.";
    if (!form.message.trim()) return "Pesan wajib diisi.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);

      await submitContactForm({
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
      });

      setSuccessMessage("Pesan berhasil dikirim.");
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Gagal mengirim pesan.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-900">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Contact
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Hubungi tim {siteName} untuk konsultasi, pertanyaan, atau kerja sama.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr,0.95fr]">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Kirim Pesan
            </h2>

            {successMessage ? (
              <div className="mt-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300">
                {successMessage}
              </div>
            ) : null}

            {error ? (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                {error}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Nama
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                    placeholder="Masukkan nama Anda"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                    placeholder="Masukkan email Anda"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Nomor HP
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                    placeholder="Masukkan nomor HP"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Subjek
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                    placeholder="Masukkan subjek pesan"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Pesan
                </label>
                <textarea
                  name="message"
                  rows="6"
                  value={form.message}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                  placeholder="Tulis pesan Anda di sini"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
              >
                {loading ? "Mengirim..." : "Kirim Pesan"}
              </button>
            </form>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Informasi Kontak
              </h2>

              <div className="mt-6 space-y-5 text-sm">
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    Nama Website
                  </div>
                  <div className="mt-1 text-gray-600 dark:text-gray-300">
                    {siteName || "-"}
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    Email
                  </div>
                  <div className="mt-1 text-gray-600 dark:text-gray-300">
                    {contactEmail ? (
                      <a
                        href={`mailto:${contactEmail}`}
                        className="hover:text-blue-600 hover:underline dark:hover:text-blue-400"
                      >
                        {contactEmail}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    Nomor HP
                  </div>
                  <div className="mt-1 text-gray-600 dark:text-gray-300">
                    {phoneNumber ? (
                      <a
                        href={`tel:${phoneNumber}`}
                        className="hover:text-blue-600 hover:underline dark:hover:text-blue-400"
                      >
                        {phoneNumber}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    WhatsApp
                  </div>
                  <div className="mt-1 text-gray-600 dark:text-gray-300">
                    {displayPhone !== "-" && normalizedWhatsappLink ? (
                      <a
                        href={normalizedWhatsappLink}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-blue-600 hover:underline dark:hover:text-blue-400"
                      >
                        {displayPhone}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    Alamat
                  </div>
                  <div className="mt-1 text-gray-600 dark:text-gray-300">
                    {address || "-"}
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    Deskripsi
                  </div>
                  <div className="mt-1 text-gray-600 dark:text-gray-300">
                    {siteDescription || "-"}
                  </div>
                </div>

                {mapsUrl && (
                  <div className="pt-2">
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                    >
                      Buka di Google Maps
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Lokasi Kami
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  Temukan lokasi {siteName} melalui peta.
                </p>
              </div>

              {mapsEmbedUrl ? (
                <div className="h-[320px] w-full">
                  <iframe
                    title="Google Maps Location"
                    src={mapsEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              ) : mapsUrl ? (
                <div className="flex min-h-[320px] flex-col items-center justify-center px-6 py-10 text-center">
                  <div className="max-w-md">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Peta belum di-embed
                    </h3>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      Link Google Maps sudah tersedia di pengaturan situs, tetapi URL embed belum diisi.
                    </p>
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-5 inline-flex items-center rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                    >
                      Buka Lokasi di Google Maps
                    </a>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[320px] items-center justify-center px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  Data peta belum tersedia di site settings.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}